import React from 'react';
import PropTypes from 'prop-types';
import createEnum from 'scripts/lib/create_enum';
import styled from 'styled-components';

import CheckFill from 'components/common/icons/fill/check-fill.jsx';
import CloseInCircleStroke from 'components/common/icons/stroke/close-in-circle-stroke.jsx';
import WarningStroke from 'components/common/icons/stroke/warning-stroke';
import InformationStroke from 'components/common/icons/stroke/information-stroke';
import ExclamationCircleIcon from 'components/lib/icons/exclamation_circle_icon';

const AlertTypes = createEnum('success', 'warning', 'error', 'errorNotification', 'info');

/**
 * @visibleName Alert
 */

const Alert = ({ 'data-aid': dataAid, children, type, className, withIcon }) => {
  const iconConfig = [
    {
      type: AlertTypes.success,
      icon: SuccessIcon,
      backgroundColor: 'green100',
    },
    {
      type: AlertTypes.warning,
      icon: WarningIcon,
      backgroundColor: 'yellow100',
    },
    {
      type: AlertTypes.error,
      icon: ErrorIcon,
      backgroundColor: 'red100',
    },
    {
      type: AlertTypes.errorNotification,
      icon: StyledExclamationCircleIcon,
      backgroundColor: 'red100',
    },
    {
      type: AlertTypes.info,
      icon: InformationIcon,
      backgroundColor: 'purple100',
    },
  ];

  const IconConfig = iconConfig.find(config => config.type === type) || iconConfig[0];
  const IconItem = IconConfig.icon;

  return (
    <>
      <AlertContainer className={className} data-aid={dataAid} role="alert" type={IconConfig.backgroundColor}>
        {withIcon && <IconItem />}
        <div>{children}</div>
      </AlertContainer>
    </>
  );
};

const AlertWarning = props => (
  <Alert {...props} type={AlertTypes.warning}>
    {props.children}
  </Alert>
);

const AlertSuccess = props => (
  <Alert {...props} type={AlertTypes.success}>
    {props.children}
  </Alert>
);

const AlertError = props => (
  <Alert {...props} type={AlertTypes.error}>
    {props.children}
  </Alert>
);

const AlertErrorNotification = props => (
  <Alert {...props} type={AlertTypes.errorNotification}>
    {props.children}
  </Alert>
);

const AlertInfo = props => (
  <Alert {...props} type={AlertTypes.info}>
    {props.children}
  </Alert>
);

const ErrorIcon = styled(CloseInCircleStroke)`
  fill: ${p => p.theme.colors.red400};
  height: ${p => p.theme.fontSize.medium};
  min-width: ${p => p.theme.fontSize.medium};
`;

const StyledExclamationCircleIcon = styled(ExclamationCircleIcon)`
  fill: ${p => p.theme.colors.red400};
  height: ${p => p.theme.fontSize.medium};
  min-width: ${p => p.theme.fontSize.medium};
`;

const WarningIcon = styled(WarningStroke)`
  fill: ${p => p.theme.colors.yellow500};
  height: ${p => p.theme.fontSize.medium};
  min-width: ${p => p.theme.fontSize.medium};
`;

const InformationIcon = styled(InformationStroke)`
  fill: ${p => p.theme.colors.purple500};
  height: ${p => p.theme.fontSize.medium};
  min-width: ${p => p.theme.fontSize.medium};
`;

const SuccessIcon = styled(CheckFill)`
  fill: ${p => p.theme.colors.green400};
  height: ${p => p.theme.fontSize.medium};
  min-width: ${p => p.theme.fontSize.medium};
`;

const AlertContainer = styled.div`
  width: 100%;
  align-items: top;
  background-color: ${({ type, theme }) => theme.colors[type]};
  font-size: 13px;
  line-height: 1.15;
  border-radius: 4px;
  display: flex;
  min-height: 56px;
  padding: 20px;

  div {
    margin-top: 1px;
  }

  svg {
    margin-right: 12px;
  }
`;

Alert.propTypes = {
  type: PropTypes.string,
  className: PropTypes.string,
  withIcon: PropTypes.bool,
};

Alert.defaultProps = {
  type: AlertTypes.success,
  withIcon: true,
};

export default Alert;
export { AlertSuccess, AlertWarning, AlertError, AlertErrorNotification, AlertInfo, AlertTypes };
